import React, { useEffect, useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import {
  LoadingType,
  PreQualificationStep,
  setAccount,
  setLoadingState,
  setPreQualificationStep,
  setSignInStep,
  SignInStep,
} from 'prequalificationV2/actions';
import { setBorrower } from 'prequalificationV2/actions/borrower';
import AccountForm, { AccountFormData } from 'prequalificationV2/components/AccountForm/AccountForm';
import FormHeader from 'prequalificationV2/components/FormHeader';
import Loader from 'components/Loader';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { AppRoutes } from 'prequalificationV2/routes/routes.enums';
import { getBorrowerByToken, login } from 'prequalificationV2/thunks';
import { navigateAfterLogin } from 'prequalificationV2/utils/navigateAfterLogin';
import { setPrequalificationPortal } from 'prequalificationV2/actions/prequalificationPortal';
import { marketingApi } from 'api/MarketingApi';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import { getConceptualDeviceId } from 'prequalificationV2/routes/routes.utils';

import styles from './SignIn.module.scss';

const SignIn = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();

  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const { email, emailError, passwordError } = useAppSelector((state) => state.account);
  const {
    borrowerId,
    email: borrowerEmail,
    phone: borrowerPhone,
    dateOfBirth: borrowerDateOfBirth,
  } = useAppSelector((state) => state.borrower);
  const [showMissingDisplayIdErrorMessage] = useState<boolean>(false);
  const [disabledEmail, setDisabledEmail] = useState<boolean>(false);

  useEffect(() => {
    setDisabledEmail(email !== '');
  }, [email]);

  useEffect(() => {
    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.SignIn,
          prequalificationVersion: PrequalificationVersion.Second,
          borrowerId: borrowerId,
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  const onSubmit = async (data: AccountFormData) => {
    if ((email || data.email) && data.password) {
      try {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

        const result = await dispatch(login({ email: email || data.email, password: data.password })).unwrap();

        dispatch(setPrequalificationPortal({ isBorrowerAuthorized: true }));

        if (result.refreshToken && result.accessToken) {
          const borrower = await dispatch(getBorrowerByToken()).unwrap();

          dispatch(
            setBorrower({
              borrowerId: borrower?.id,
              email: borrowerEmail || borrower?.variables.borrower_email,
              phone: borrowerPhone || borrower?.variables.borrower_phone,
              dateOfBirth: borrowerDateOfBirth || borrower?.variables.borrower_date_of_birth,
              firstName: borrower?.variables.borrower_first_name,
              lastName: borrower?.variables.borrower_last_name,
              streetNumber: borrower?.variables.borrower_home_address.street_number!,
              streetName: borrower?.variables.borrower_home_address.street_name!,
              city: borrower?.variables.borrower_home_address.city!,
              state: borrower?.variables.borrower_home_address.state_or_province!,
              zip: borrower?.variables.borrower_home_address.zip_or_postal_code!,
              personalIdNumber: borrower?.variables.borrower_personal_id_number,
              isLocked: borrower?.locked,
              borrowerFraud: borrower?.variables.borrower_fraud,
            }),
          );
          dispatch(
            setAccount({
              email: borrower.variables.borrower_email,
              phone: borrower.variables.borrower_phone,
              id: borrower.id,
            }),
          );

          try {
            await navigateAfterLogin({ isLocked: !!borrower.locked, dispatch: dispatch, navigator: navigator });
          } catch (error: any) {
            dispatch(setSignInStep(SignInStep.NotFoundContact));
          }
        }
      } catch (error: any) {
        dispatch(
          setAccount({
            emailError: 'Invalid Credentials. Please check your Email and Password, and try again.',
            passwordError: ' ',
          }),
        );
      } finally {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }
    }
  };

  const handleForgotPasswordClick = () => {
    dispatch(setSignInStep(SignInStep.ForgotPassword));
  };

  const renderInfoMessage = () => {
    return (
      <div>
        Seems like you don’t have an active application,
        <a href="/" className={styles.redirectLink}>
          Click Here
        </a>
        to create a new one.
      </div>
    );
  };

  const formMethods = useForm({
    defaultValues: {
      email: email,
      password: '',
    },
  });

  const handleNoLoanPortal = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIdentity));
    navigator(AppRoutes.Prequalification);
  };

  const getSubTitle = () => {
    if (email) {
      return 'It looks like you have applied before, please log in to verify your identity';
    }

    return '';
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <FormHeader
            title="Log in to your HFS Personal Loan Portal"
            subtitle={getSubTitle()}
            classNameWrapper={styles.customFormHeader}
          />
          <FormProvider {...formMethods}>
            <AccountForm
              passwordLabel="Password"
              showEmailField
              showPasswordField
              showForgotPassword
              isEmailDisabled={disabledEmail}
              showNotification={showMissingDisplayIdErrorMessage}
              notificationTitle={renderInfoMessage}
              handleForgotPassword={handleForgotPasswordClick}
              buttonLabel="Log In"
              backButton={!email && !borrowerId ? undefined : PreQualificationStep.BorrowerIdentity}
              backLink={!email && !borrowerId ? undefined : AppRoutes.Prequalification}
              {...{ emailError: emailError, passwordError: passwordError, onSubmit: onSubmit }}
            />
          </FormProvider>
          {!email && (
            <div className={styles.noLoanPortal}>
              Don't have a Loan Portal? Submit loan inquiry{' '}
              <span className={styles.noLoanPortalLink} onClick={handleNoLoanPortal}>
                here.
              </span>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default SignIn;
